"use client";

import {
  ReferralWorkflowTimeline,
  type ReferralWorkflowStatus,
  type ReferralWorkflowStep,
} from "@/configuration/groups/usrc/components/referral-timeline";
import { IntakeManagerDocumentRoute } from "@/src/lib/ehr/intake-manager/document-route";
import { StepKey } from "@/src/lib/workflows/steps/types";
import { ensurePatientOutputSchema } from "@/src/models/ensure-patient-output";
import { TaskStatus, type Task } from "@/src/models/task";
import { TaskStepStatus, type TaskStep } from "@/src/models/task-step";

// Step outputs are read field by field rather than through the producers' zod schemas: those live
// beside the EHR integrations, which pull server-only modules into the client bundle.
function outputString(step: TaskStep | undefined, key: string): string | null {
  const value = step?.output?.[key];

  return typeof value === "string" && value.trim() ? value : null;
}

function outputNumber(step: TaskStep | undefined, key: string): number | null {
  const value = step?.output?.[key];

  return typeof value === "number" ? value : null;
}

function stepStatus(
  step: TaskStep | undefined,
  confirmed: boolean,
  options: { needsAction?: boolean; skipped?: boolean } = {}
): ReferralWorkflowStatus {
  if (!step) return "not_run";
  if (step.status === TaskStepStatus.FAILED) return "failed";
  if (options.needsAction) return "needs_action";
  if (step.status === TaskStepStatus.ASYNC || step.status === TaskStepStatus.PAUSED) return "waiting";
  // Early exits are persisted as completed too, so require success evidence before the checkmark.
  if (step.status === TaskStepStatus.COMPLETED && confirmed) return options.skipped ? "not_needed" : "automated";

  return "needs_action";
}

export function UsrcReferralSteps({ task, taskSteps }: { task: Task; taskSteps: TaskStep[] }) {
  const orderedTaskSteps = taskSteps.toSorted((a, b) => a.stepIndex - b.stepIndex);
  const findStep = (key: StepKey) => orderedTaskSteps.find((step) => step.stepKey === key);

  const lookupStep = findStep(StepKey.USRC_REGISTRATION_LOOKUP);
  const patientStep = findStep(StepKey.ENSURE_PATIENT);
  const questionnaireStep = findStep(StepKey.USRC_REGISTRATION_QUESTIONNAIRE);
  const uploadStep = findStep(StepKey.EHR_DOC_UPLOAD);
  const supportingStep = findStep(StepKey.SUPPORTING_SECTIONS_UPLOAD);
  const noteStep = findStep(StepKey.USRC_REGISTRATION_NOTE);
  const conversationStep = findStep(StepKey.USRC_REGISTRATION_CONVERSATION);

  const disposition = outputString(lookupStep, "disposition");
  const matchedRegistrationId = outputNumber(lookupStep, "registrationId");
  const registration = ensurePatientOutputSchema.safeParse(patientStep?.output);
  const unmappedClassification =
    outputString(uploadStep, "outcome") === "unmapped_document_type"
      ? outputString(uploadStep, "classification")
      : null;
  const unmapped = outputString(uploadStep, "outcome") === "unmapped_document_type";
  const discharged = disposition === "involuntarily_discharged";
  const triageFiles = outputString(lookupStep, "documentRoute") === IntakeManagerDocumentRoute.TRIAGE;
  const questionnaireOutcome = outputString(questionnaireStep, "outcome");
  const noQuestionnaire = questionnaireOutcome === "not_applicable";
  const questionnaireUnpostable = questionnaireOutcome === "unpostable";
  const uploaded = outputString(uploadStep, "fileId") !== null;
  const supportingUploads = Array.isArray(supportingStep?.output?.uploads) ? supportingStep.output.uploads : null;
  const supportingCount = supportingUploads ? supportingUploads.length : null;
  const supportingDone = supportingStep?.status === TaskStepStatus.COMPLETED;

  const steps: ReferralWorkflowStep[] = [
    {
      title: "Find the patient's registration",
      status: stepStatus(lookupStep, disposition !== null, { needsAction: discharged }),
      summary: discharged
        ? "This patient was involuntarily discharged. The referral stops here."
        : "Searched Intake Manager for an existing registration for this patient.",
      content: disposition ? (
        <p className="text-sm">
          {disposition === "update"
            ? `Matched existing registration ${matchedRegistrationId ?? "(id not recorded)"}.`
            : disposition === "create"
              ? "No existing registration; a new one was created in the next step."
              : "Intake Manager reports this patient as involuntarily discharged. Do not register them again."}
        </p>
      ) : undefined,
    },
    {
      title: "Create or update the registration",
      status: stepStatus(patientStep, registration.success),
      summary: "Registered the patient in Intake Manager, or updated the registration the lookup found.",
      content: registration.success ? (
        <p className="text-sm">
          {registration.data.created ? "New registration" : "Existing registration"}{" "}
          <strong>{registration.data.patientEhrId}</strong>. Use this ID to find the chart in Intake Manager.
        </p>
      ) : undefined,
    },
    {
      title: "Answer the dialysis questionnaire",
      // Posting nothing means two different things: no questionnaire to post is genuinely nothing
      // to do, while a questionnaire we cannot send leaves the answers owed by hand.
      status: stepStatus(questionnaireStep, true, {
        needsAction: questionnaireUnpostable,
        skipped: noQuestionnaire,
      }),
      summary: noQuestionnaire
        ? "This packet did not include a dialysis questionnaire."
        : questionnaireUnpostable
          ? "No answers were posted. Enter them in Intake Manager by hand."
          : "Posted the isolation, equipment and mobility answers to the registration.",
      content: questionnaireUnpostable ? (
        <p className="text-sm">
          USRC has not published the questionnaire question and answer IDs, so the extracted answers cannot be posted
          yet. Enter them in Intake Manager by hand.
        </p>
      ) : undefined,
    },
    {
      title: "Upload the referral packet",
      status: stepStatus(uploadStep, uploaded, { needsAction: unmapped }),
      summary: unmapped
        ? "The referral packet was not uploaded."
        : discharged
          ? "Not reached. The lookup step sent the original file to triage."
          : triageFiles
            ? "Sent the original file to the Intake Manager triage queue."
            : "Indexed the referral PDF to the registration in Intake Manager.",
      content: unmapped ? (
        <p className="text-sm">
          No Intake Manager document type is configured for{" "}
          <strong>{unmappedClassification ?? "this classification"}</strong>, so the document was not uploaded. File it
          by hand and ask for the document type ID to be added.
        </p>
      ) : undefined,
    },
    {
      title: "Upload supporting documents",
      status: stepStatus(supportingStep, supportingDone, {
        skipped: triageFiles || supportingCount === 0,
      }),
      summary: triageFiles
        ? "Included in the original file sent to triage."
        : supportingCount === 0
          ? "This packet had no additional supporting clinical records."
          : supportingCount !== null
            ? `Indexed ${supportingCount} supporting document${supportingCount === 1 ? "" : "s"} to the registration.`
            : "Index clinical records, labs, imaging, and questionnaires to the registration in Intake Manager.",
      content:
        !triageFiles && supportingCount !== null && supportingCount > 0 ? (
          <p className="text-sm">
            Indexed {supportingCount} supporting document{supportingCount === 1 ? "" : "s"} (labs, notes,
            questionnaires) directly to the registration in Intake Manager.
          </p>
        ) : undefined,
    },
    {
      title: "Add the registration note",
      status: stepStatus(noteStep, true),
      summary: "Posted a staff Note with pending medical-records and clearance flags (not the Referral Notes field).",
      content: <p className="text-sm">Intake Manager returns no confirmation for this write. Check the note exists.</p>,
    },
    {
      title: "Send the handoff conversation",
      status: stepStatus(conversationStep, true, { skipped: triageFiles }),
      summary: triageFiles
        ? "The admissions handoff was not sent."
        : "Posted the admissions handoff message on the registration's conversation topic.",
      content: !triageFiles ? (
        <p className="text-sm">Intake Manager returns no confirmation for this write. Check the message exists.</p>
      ) : undefined,
    },
  ];

  const isCompleted = task.status === TaskStatus.COMPLETED;
  const isProcessing = task.status === TaskStatus.PROCESSING;
  const hasFailedStep = steps.some((step) => step.status === "failed");
  const needsAttention = steps.some((step) => step.status === "needs_action" || step.status === "failed");
  const hasUnfinishedStep = steps.some((step) => step.status === "not_run" || step.status === "waiting");

  steps.push({
    title: "Verify in Intake Manager",
    // Never "verified": getFinalStatus returns COMPLETED automatically, so a completed task means
    // automation finished, not that anyone checked it.
    status: hasFailedStep
      ? "failed"
      : needsAttention
        ? "needs_action"
        : hasUnfinishedStep && !isCompleted
          ? "waiting"
          : "verify",
    summary: isCompleted
      ? "Automation finished and completed this task on its own. Nobody has checked Intake Manager yet."
      : hasFailedStep || needsAttention
        ? "Resolve the outstanding steps above before verifying this task."
        : hasUnfinishedStep
          ? "The workflow is still running. Verify the results when it finishes."
          : triageFiles
            ? "Confirm the registration, questionnaire, referral document and staff note in Intake Manager."
            : "Confirm the registration, questionnaire, referral document, staff note and conversation in Intake Manager.",
    content: (
      <p className="text-sm">
        Open the registration in Intake Manager and confirm the referral landed.{" "}
        {triageFiles
          ? "The staff note returns no confirmation, so check it by hand."
          : "The staff note and conversation return no confirmation, so check those by hand."}
      </p>
    ),
  });

  return (
    <ReferralWorkflowTimeline
      steps={steps}
      isProcessing={isProcessing}
      waitingMessage={
        !isCompleted && taskSteps.length === 0
          ? "No Intake Manager sync steps have been recorded for this task yet."
          : undefined
      }
    />
  );
}
